import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import LanguageSelector from './components/LanguageSelector';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import District9 from './pages/District9';
import StaffDirectory from './pages/StaffDirectory';
import Partnerships from './pages/Partnerships';
import Students from './pages/Students';
import Calendar from './pages/Calendar';
import Contact from './pages/Contact';
import Enrollment from './pages/Enrollment';
import ParentAdvisory from './pages/ParentAdvisory';
import StayInformed from './pages/StayInformed';
import routes from './routes.json';
import './App.css';

// Single source of truth for pages lives in src/routes.json, which drives BOTH the
// React Router config below AND the static prerender list (scripts/prerender.mjs).
// To ADD a page: create the component, import it above, add it to `registry`, and add
// an entry to src/routes.json. To REMOVE a page: delete all four (component, import,
// registry entry, routes.json entry).
const registry = {
  Home,
  AboutUs,
  District9,
  StaffDirectory,
  Partnerships,
  Students,
  Calendar,
  Contact,
  Enrollment,
  ParentAdvisory,
  StayInformed,
};

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  // Suppress generic cross-origin "Script error." noise from Google Translate's iframe scripts.
  // These have no filename/lineno and are not actionable; without this they trigger the dev
  // error overlay and pollute logs without surfacing the underlying cause.
  useEffect(() => {
    const onError = (e) => {
      if (e.message === 'Script error.' && !e.filename && !e.lineno) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    window.addEventListener('error', onError, true);
    return () => window.removeEventListener('error', onError, true);
  }, []);

  useEffect(() => {
    // Google Translate appends a hidden "voting form" tooltip (#goog-gt-tt) directly to
    // <body>, outside our #google_translate_element wrapper. Its 5 unlabeled inputs trigger
    // WAVE's label_missing rule. The tooltip drives the optional hover-to-rate feature only,
    // which we don't surface — so remove it outright. Banner / balloon frames are also marked
    // aria-hidden + inert in case Google re-injects them.
    const cleanup = () => {
      document.querySelectorAll('#goog-gt-tt').forEach((n) => n.remove());
      document.querySelectorAll('.goog-te-banner-frame, .goog-te-balloon-frame, .goog-te-ftab').forEach((node) => {
        if (!node.hasAttribute('aria-hidden')) node.setAttribute('aria-hidden', 'true');
        if (!node.hasAttribute('inert')) node.setAttribute('inert', '');
      });
      document.querySelectorAll('.goog-te-combo').forEach((sel) => {
        if (!sel.hasAttribute('aria-label')) sel.setAttribute('aria-label', 'Google Translate language selector (hidden — use the visible language toggle instead)');
      });
    };
    cleanup();
    const observer = new MutationObserver(cleanup);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Ensure the widget container exists and is empty before init (prerendered HTML may
    // contain a stale instance, which would cause Google Translate to render twice).
    const el = document.getElementById('google_translate_element');
    if (el) el.innerHTML = '';

    // Google's loader defines window.google.translate BEFORE TranslateElement exists, then
    // calls ?cb= once it is ready. The prerendered HTML already carries the loader <script>,
    // so this effect often runs in that half-loaded window. Calling the constructor then
    // threw "TranslateElement is not a constructor" inside the effect, and with no error
    // boundary React unmounted the whole app: a blank page on roughly one load in three.
    // Translation is an enhancement; it must never be able to take the page down.
    const translateReady = () =>
      !!(window.google && window.google.translate && typeof window.google.translate.TranslateElement === 'function');

    window.googleTranslateElementInit = function () {
      if (!translateReady()) return;
      try {
        // eslint-disable-next-line no-new
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,es,fr',
            autoDisplay: false,
          },
          'google_translate_element'
        );
      } catch (err) {
        // Leave the page in English rather than crash it.
      }
    };

    // Initialize as soon as the API is fully loaded and the mount is still empty. The loader's
    // own ?cb= call can fire before this effect has defined the callback (the prerendered HTML
    // loads the script early), so don't rely on it alone: check now, then keep checking briefly.
    const initWhenReady = () => {
      if (!translateReady()) return false;
      const host = document.getElementById('google_translate_element');
      if (host && host.childElementCount === 0) window.googleTranslateElementInit();
      return true;
    };

    let tries = 0;
    let timer = null;
    if (!initWhenReady()) {
      timer = window.setInterval(() => {
        tries += 1;
        if (initWhenReady() || tries > 60) window.clearInterval(timer);
      }, 250);
    }

    // Load the script unless it is already on the page (prerendered HTML carries it).
    const existing = document.querySelector('script[src*="translate.google.com/translate_a/element.js"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.head.appendChild(script);
    }
    return () => { if (timer) window.clearInterval(timer); };
  }, []);

  return (
    <Router>
      <div className="App">
        <header role="banner">
          <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:bg-[#febc04] focus:text-[#123f1e] focus:font-bold focus:rounded-lg focus:shadow-lg">
            Skip to main content
          </a>
          <Navbar />
          <LanguageSelector />
        </header>
        <ScrollToTop />
        <main id="main-content">
          <Routes>
            {routes.map(({ path, component }) => {
              const C = registry[component];
              return C ? <Route key={path} path={path} element={<C />} /> : null;
            })}
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
